import { isRegistrationClosed } from './registration-closing';
import type { CapacityState } from './data/mock-schedule';

/** The capacity-relevant fields of a schedule row. */
export interface CapacityInput {
  spotsLeft: number;
  spotsTotal?: number;
  capacityState: CapacityState;
}

/** The embed options that influence capacity display. */
export interface CapacityDisplayOpts {
  capacityMode?: 'all' | 'low-only' | 'none';
  capacityThreshold?: number;
  hideCapacity?: boolean;
}

/**
 * Capacity badge vocabulary, mirroring the dashboard-customer booking flow:
 *  - waitlist with people:  "⏳ {n} on the waitlist"
 *  - waitlist, empty:       "✨ Be the first to join the waitlist!"
 *  - full (0 left):         "Full"
 *  - low availability:      "🔥 Only {n} left!"  (spotsLeft <= 2 OR <= 33% of total)
 *  - healthy count:         "{n} left"
 *  - uncapped/unknown:      "Open" in "Always" mode (so every class shows a
 *                           capacity line), hidden otherwise
 *
 * Returns `null` when the badge should not be shown (capacity hidden, "none"
 * mode, "low-only" mode above the threshold, or unknown capacity outside
 * "Always" mode).
 */
export function capacityBadge(
  c: CapacityInput,
  opts: CapacityDisplayOpts = {},
): { text: string; waiting: boolean } | null {
  const mode = opts.capacityMode ?? 'all';
  if (mode === 'none' || opts.hideCapacity) return null;

  if (c.capacityState === 'waitlist') {
    const n = c.spotsLeft; // waitlisted count
    return {
      text:
        n > 0
          ? `⏳ ${n} on the waitlist`
          : '✨ Be the first to join the waitlist!',
      waiting: true,
    };
  }

  // Unknown / uncapped capacity — live data for a class with no registration
  // cap (unlimited). In "Always" mode surface a neutral "Open" so every class
  // carries a capacity line; "Low only" has no low signal to show here.
  if (c.spotsLeft < 0) {
    return mode === 'all' ? { text: 'Open', waiting: false } : null;
  }

  const threshold = opts.capacityThreshold ?? 5;
  if (mode === 'low-only' && c.spotsLeft > threshold) return null;

  // Full (no waitlist) reads as "Full" rather than "🔥 Only 0 left!".
  if (c.spotsLeft === 0) return { text: 'Full', waiting: false };

  // Low-availability "fire" emphasis — same rule as the dashboard-customer
  // flow: 2 or fewer spots, or 33% or less of total capacity remaining.
  const total = c.spotsTotal ?? 0;
  const low = c.spotsLeft <= 2 || (total > 0 && c.spotsLeft / total <= 0.33);
  if (low) return { text: `🔥 Only ${c.spotsLeft} left!`, waiting: false };

  return { text: `${c.spotsLeft} left`, waiting: false };
}

/** The subset of occurrence fields needed to decide whether it's already
 * started, relative to "now". */
export interface OccurrenceTiming {
  /** Real UTC instant the occurrence starts (live rows: `ActivityDto.start`;
   * mock rows: computed by `isoForWallClock`). Preferred over `date` because
   * `date` alone doesn't reliably carry a time-of-day (mock rows are
   * midnight-anchored; only `startIso` pins the actual start time). */
  startIso?: string;
  endIso?: string;
  duration?: string;
  openRegistrationTimeFrame?: boolean;
  registrationClosesAt?: string | null;
  date: Date;
}

/** Timing plus the only row state that suppresses an otherwise future booking
 * action. Waitlist rows deliberately remain eligible. */
export interface ActionableOccurrence extends OccurrenceTiming {
  cancelled?: boolean;
}

/**
 * The real instant `c` starts at. Extracted so every "when does this happen"
 * decision (is it past? which day does it anchor to?) resolves the instant the
 * same way and the answers can never disagree with each other.
 */
export function occurrenceInstant(c: OccurrenceTiming): Date {
  const withInstant = c.startIso ? new Date(c.startIso) : c.date;
  return isNaN(withInstant.getTime()) ? c.date : withInstant;
}

/** Past styling starts at the end, independently of an earlier custom close.
 * Legacy timing-less preview rows retain their conservative start fallback. */
export function isPastOccurrence(c: OccurrenceTiming, now: Date): boolean {
  if (c.endIso !== undefined) {
    const end = new Date(c.endIso).getTime();
    return !Number.isFinite(end) || end <= now.getTime();
  }
  const minutes = c.duration?.match(/^(\d+)\s*min/);
  const start = occurrenceInstant(c).getTime();
  return minutes ? start + Number(minutes[1]) * 60_000 <= now.getTime()
    : start < now.getTime();
}

/** Start-of-day (local wall-clock midnight) for `d`. */
export function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * The calendar day `instant` falls on **in `tz`**, returned as a local-midnight
 * Date so it drops straight into the day math the component already does
 * (`sameDay`, `addDays`, `getDate()`), all of which reads local wall-clock
 * parts.
 *
 * Why this exists rather than a bare `new Date()`: every day-level decision in
 * the schedule — "is this today?", which pill the week strip highlights, the
 * "Today, …" section header — has to be made in the zone the visitor is
 * READING times in, not whichever zone their device happens to be set to. A
 * visitor in Tokyo looking at an Eastern studio is 13–14 hours ahead: at 9am
 * Tokyo it is still the previous day in New York, so anchoring on the browser's
 * raw date would skip a whole day of bookable classes. Deriving the parts
 * through Intl also means the day rolls over at midnight in `tz` — never at
 * UTC midnight, and never at the device's midnight.
 *
 * Falls back to the device's own calendar day when `tz` is empty or not a valid
 * IANA name (Intl throws) — the same defensive posture as `formatZonedTime`.
 */
export function startOfZonedDay(instant: Date, tz?: string): Date {
  if (tz) {
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).formatToParts(instant);
      const part = (type: string): number =>
        Number(parts.find((p) => p.type === type)?.value);
      const [year, month, day] = [part('year'), part('month'), part('day')];
      if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
        return new Date(year, month - 1, day);
      }
    } catch {
      /* unusable zone — fall through to the device's own day */
    }
  }
  return startOfDay(instant);
}

/**
 * The occurrence the schedule should land on for "default to today **and the
 * next event**".
 *
 * Returns the earliest non-canceled occurrence that remains bookable —
 * decided with {@link isPastOccurrence}, so it's a real-instant
 * comparison and a 6pm class still counts as "today" at 5pm but not at 7pm.
 * Waitlist rows remain eligible because they still expose an action. In the
 * ordinary case that day IS today, which is the "default to today" half. When
 * today has nothing left it falls FORWARD to the next available day.
 *
 * Falling forward — rather than staying on an empty today with a "next class
 * is Wednesday" pointer — is the deliberate choice: the widget exists to get
 * people booking, and the list below it already renders forward from today, so
 * anchoring the strip on the next real class is the only option where the
 * highlighted pill and the content underneath it agree without a second click.
 * The "Today" pill stays available to come back.
 *
 * Occurrences before `start` (a configured `ScheduleOptions.startDate`) are
 * ignored so an org that opens its schedule on a future date is honored, and
 * callers can use `start` as the date fallback when nothing upcoming is loaded.
 *
 * Deterministic: it takes the minimum instant rather than trusting the caller's
 * sort order, so first paint is identical on every load.
 */
/** The exact occurrence behind {@link initialAnchorDate}. */
export function initialAnchorOccurrence<T extends ActionableOccurrence>(
  occurrences: readonly T[],
  now: Date,
  start: Date,
  tz?: string,
): T | null {
  const floor = startOfDay(start).getTime();
  let bestInstant = Infinity;
  let best: T | null = null;
  for (const c of occurrences) {
    if (c.cancelled || isPastOccurrence(c, now) || isRegistrationClosed(c, now)) continue;
    const instant = occurrenceInstant(c);
    const day = startOfZonedDay(instant, tz);
    if (day.getTime() < floor) continue;
    if (instant.getTime() < bestInstant) {
      bestInstant = instant.getTime();
      best = c;
    }
  }
  return best;
}

/** Calendar day of {@link initialAnchorOccurrence}, or `start` when none. */
export function initialAnchorDate(
  occurrences: readonly ActionableOccurrence[],
  now: Date,
  start: Date,
  tz?: string,
): Date {
  const best = initialAnchorOccurrence(occurrences, now, start, tz);
  return best
    ? startOfZonedDay(occurrenceInstant(best), tz)
    : startOfDay(start);
}

/** Google Maps "search" deep link for a location query. */
export function mapsHref(query: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    query,
  )}`;
}

/**
 * Prefer the location's full formatted address for the Maps link (so it lands
 * on the actual studio) and fall back to the short location name when no
 * address is available.
 */
export function preferredLocationQuery(c: {
  location: string;
  locationAddress?: string;
}): string {
  return c.locationAddress || c.location;
}

/** Baseline opening rule shared with the customer app; equality is open. */
export function isNotYetOpen(
  occurrence: { registrationOpenAt?: string | null; openRegistrationTimeFrame?: boolean },
  now: Date,
): boolean {
  if (occurrence.openRegistrationTimeFrame || !occurrence.registrationOpenAt) return false;
  return now.getTime() < new Date(occurrence.registrationOpenAt).getTime();
}

/** Calendar-only opaque class color, with the more readable black/white ink.
 * Reject other CSS values so unsupported legacy colors retain the embed theme. */
export function classColorStyle(color?: string): string {
  if (
    typeof color !== "string" ||
    !/^#(?:[\da-f]{3}|[\da-f]{6})$/i.test(color.trim())
  )
    return "";
  const raw = color.trim().slice(1);
  const hex = (
    raw.length === 3 ? [...raw].map((digit) => digit + digit).join("") : raw
  ).toLowerCase();
  const channels = [0, 2, 4].map((offset) => {
    const value = parseInt(hex.slice(offset, offset + 2), 16) / 255;
    return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  });
  const luminance =
    channels[0] * 0.2126 + channels[1] * 0.7152 + channels[2] * 0.0722;
  const ink =
    (luminance + 0.05) / 0.05 >= 1.05 / (luminance + 0.05)
      ? "#000000"
      : "#ffffff";
  return `--cal-bar-bg:#${hex};--cal-bar-ink:${ink};`;
}
